/**
 * REUSABLE HOOK - useApi
 * Custom hook for API calls with loading and error states
 * Can be used in any React project with API calls
 */

import { useState, useCallback } from 'react';

/**
 * useApi - API call hook
 * @returns {Object} API state and methods
 */
export const useApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  /**
   * Execute an API call
   * @param {Function} apiFunction - Async function to execute
   * @param {Object} options - Options
   * @returns {Promise} API result
   */
  const execute = useCallback(async (apiFunction, options = {}) => {
    const {
      onSuccess,
      onError,
      resetData = true,
      showLoading = true,
    } = options;

    try {
      if (showLoading) {
        setLoading(true);
      }

      if (resetData) {
        setError(null);
      }

      const result = await apiFunction();

      setData(result);

      if (onSuccess) {
        onSuccess(result);
      }

      return result;
    } catch (err) {
      console.error('API Error:', err);
      setError(err.message || 'Une erreur est survenue');

      if (onError) {
        onError(err);
      }

      throw err;
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, []);

  /**
   * Reset hook state
   */
  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
    setData(null);
  }, []);

  /**
   * Set data manually
   */
  const setDataManually = useCallback((newData) => {
    setData(newData);
  }, []);

  /**
   * Set error manually
   */
  const setErrorManually = useCallback((newError) => {
    setError(newError);
  }, []);

  return {
    loading,
    error,
    data,
    execute,
    reset,
    setData: setDataManually,
    setError: setErrorManually,
  };
};

export default useApi;
